import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useToast, ToastContainer } from "../../components/common/Toast";
import { apiService } from "../../services/apiService";

const DEFAULT_MESSAGE = `You've been invited to join SendUpdates.ai, the platform designed specifically for venture capital firms to streamline portfolio company updates and insights.

Click the link below to accept your invitation and create your account.

If you have any questions, please don't hesitate to reach out.

Best regards,
The SendUpdates.ai Team`;

const InviteVC: React.FC = () => {
  const navigate = useNavigate();
  const {
    toasts,
    removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();

  const [email, setEmail] = useState("");
  const [customMessage, setCustomMessage] = useState(DEFAULT_MESSAGE);
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      showError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const messageToSend = useCustomMessage ? customMessage : undefined;
      await apiService.createVCInvite(email, messageToSend);

      showSuccess("Invite sent successfully!");

      // Reset form
      setEmail("");
      setCustomMessage(DEFAULT_MESSAGE);
      setUseCustomMessage(false);

      // Redirect back to admin dashboard after a short delay
      setTimeout(() => {
        navigate("/admin");
      }, 2000);
    } catch (error) {
      console.error("Error sending invite:", error);
      showError(
        error instanceof Error ? error.message : "Failed to send invite"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Invite VC Firm"
        subtitle="Send an invitation to a venture capital firm to join the SendUpdates.ai platform"
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Email Address <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vc@example.com"
              disabled={loading}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
          </div>

          {/* Custom Message Toggle */}
          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomMessage}
                onChange={(e) => setUseCustomMessage(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 text-purple-600 bg-gray-800 border-gray-600 rounded focus:ring-purple-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-sm font-medium text-gray-300">
                Customize invitation message
              </span>
            </label>
          </div>

          {/* Custom Message Textarea */}
          {useCustomMessage && (
            <div>
              <label
                htmlFor="customMessage"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Custom Message
              </label>
              <textarea
                id="customMessage"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                disabled={loading}
                rows={12}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
                placeholder="Enter your custom invitation message..."
              />
              <p className="mt-2 text-sm text-gray-400">
                Note: The invite link will be automatically added to the email.
                Use [INVITE_LINK] as a placeholder if you want to control its
                position.
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-300">
                <p className="font-semibold mb-1">What happens next?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200">
                  <li>
                    An email will be sent to the provided address with a unique
                    invite link
                  </li>
                  <li>The invite will expire after 7 days</li>
                  <li>
                    Once accepted, the user will be automatically assigned the
                    VC role
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              disabled={loading}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="px-8 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                "Send Invitation"
              )}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default InviteVC;
